<template>
  <video
    ref="videoRef"
    v-bind="$attrs"
    :src="activeSrc"
    :preload="isActive ? preload : 'none'"
    @loadedmetadata="$emit('loadedmetadata', $event)"
    @error="$emit('error', $event)"
  />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  src: { type: String, default: '' },
  /**
   * 仅在进入可视区后生效的 preload。
   * 列表页建议用 metadata，这样能显示首帧/缩略图，但不会像 auto 那样更重。
   */
  preload: { type: String, default: 'metadata' }
})

defineEmits(['loadedmetadata', 'error'])

const videoRef = ref(null)
const activeSrc = ref('')
const isActive = ref(false)

let observer = null

function activate() {
  isActive.value = true
  if (!activeSrc.value && props.src) {
    activeSrc.value = props.src
    // 触发一次 load，确保能拿到首帧（否则部分浏览器 preload=metadata 也可能不立即渲染）
    nextTick(() => {
      const el = videoRef.value
      el?.load?.()
    })
  }
}

function deactivate() {
  const el = videoRef.value
  if (!el) return
  try {
    el.pause()
    // 释放解码/网络资源：清空 src + load()
    isActive.value = false
    activeSrc.value = ''
    el.load?.()
  } catch {
    // ignore
  }
}

watch(
  () => props.src,
  (newSrc) => {
    // 如果已经激活过，src 变化要同步（例如数据刷新）
    if (activeSrc.value) activeSrc.value = newSrc || ''
  }
)

onMounted(() => {
  const el = videoRef.value
  if (!el) return

  // 兼容性兜底：不支持 IntersectionObserver 就直接加载
  if (!('IntersectionObserver' in window)) {
    activate()
    return
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) activate()
        else deactivate()
      }
    },
    {
      // 提前一点加载，避免滚动到可见时闪一下
      root: null,
      rootMargin: '200px 0px',
      threshold: 0.01
    }
  )

  observer.observe(el)
})

onBeforeUnmount(() => {
  if (observer) observer.disconnect()
  observer = null
})
</script>


